// Unified employee incident service (for backward compatibility and convenience)
import { employeeIncidentRegisterService } from './services/employeeIncidentRegister.service';
import { employeeIncidentListService } from './services/employeeIncidentList.service';
import { employeeIncidentGetService } from './services/employeeIncidentGet.service';
import { employeeIncidentExportService } from './services/employeeIncidentExport.service';
import { employeeIncidentListManagementService } from './services/employeeIncidentListManagement.service';
import { employeeIncidentExportManagementService } from './services/employeeIncidentExportManagement.service';
import { employeeIncidentApproveService } from './services/employeeIncidentApprove.service';
import { employeeIncidentRejectService } from './services/employeeIncidentReject.service';
import { employeeIncidentBulkApproveService } from './services/employeeIncidentBulkApprove.service';
import { employeeIncidentBulkRejectService } from './services/employeeIncidentBulkReject.service';
import type { ApiResponse } from '@/api/shared';
import type {
  RegisterIncidentInput,
  RegisterIncidentResult,
} from './types/employeeIncidentRegister.types';
import type { LC_ListIncidentsIn, IncidentListResult } from './types/employeeIncidentList.types';
import type { IncidentDetail } from './types/employeeIncidentGet.types';
import type {
  LC_ListIncidentsManagementIn,
  IncidentManagementPage,
} from './types/employeeIncidentListManagement.types';
import type {
  LC_ExportIncidentsManagementIn,
  IncidentManagementExportHybridResult,
} from './types/employeeIncidentExportManagement.types';
import type {
  LC_ApproveIncidentIn,
  LC_ApproveIncidentOut,
} from './types/employeeIncidentApprove.types';
import type {
  LC_RejectIncidentIn,
  LC_RejectIncidentOut,
} from './types/employeeIncidentReject.types';
import type {
  LC_BulkApproveIncidentsIn,
  LC_BulkApproveIncidentsOut,
} from './types/employeeIncidentBulkApprove.types';
import type {
  LC_BulkRejectIncidentsIn,
  LC_BulkRejectIncidentsOut,
} from './types/employeeIncidentBulkReject.types';

/**
 * Unified EmployeeIncidentService that delegates to modular services
 * Maintains backward compatibility with existing code
 */
export class EmployeeIncidentService {
  // Registration operations
  async register(input: RegisterIncidentInput): Promise<ApiResponse<RegisterIncidentResult>> {
    return employeeIncidentRegisterService.register(input);
  }

  // List operations (self-service, "Mis incidentes")
  async list(params: LC_ListIncidentsIn): Promise<ApiResponse<IncidentListResult>> {
    return employeeIncidentListService.list(params);
  }

  // Detail operations
  async getDetail(employeeId: number, incidentId: number): Promise<ApiResponse<IncidentDetail>> {
    return employeeIncidentGetService.getDetail(employeeId, incidentId);
  }

  // Export operations
  async exportIncidents(
    employeeId: number,
    startDate: string,
    endDate: string,
    errorTypeId?: number | null,
    status?: string | null,
  ): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    return employeeIncidentExportService.exportIncidents(
      employeeId, startDate, endDate, errorTypeId, status,
    );
  }

  // List management operations (admin-wide, ASIS-5244)
  async listManagement(
    params: LC_ListIncidentsManagementIn,
    signal?: AbortSignal,
  ): Promise<ApiResponse<IncidentManagementPage>> {
    return employeeIncidentListManagementService.listManagement(params, signal);
  }

  // Export management operations (admin-wide, hybrid sync/async, ASIS-5245)
  async exportManagement(
    params: LC_ExportIncidentsManagementIn,
  ): Promise<ApiResponse<IncidentManagementExportHybridResult>> {
    return employeeIncidentExportManagementService.exportManagement(params);
  }

  // Approval operations (individual approve, ASIS-5247)
  approve(
    incidentId: number,
    input: LC_ApproveIncidentIn,
  ): Promise<ApiResponse<LC_ApproveIncidentOut>> {
    return employeeIncidentApproveService.approve(incidentId, input);
  }

  // Rejection operations (individual reject, ASIS-5248)
  reject(
    incidentId: number,
    input: LC_RejectIncidentIn,
  ): Promise<ApiResponse<LC_RejectIncidentOut>> {
    return employeeIncidentRejectService.reject(incidentId, input);
  }

  // Bulk approval operations (ASIS-5249)
  bulkApprove(
    input: LC_BulkApproveIncidentsIn,
  ): Promise<ApiResponse<LC_BulkApproveIncidentsOut>> {
    return employeeIncidentBulkApproveService.bulkApprove(input);
  }

  // Bulk rejection operations (ASIS-5250)
  bulkReject(
    input: LC_BulkRejectIncidentsIn,
  ): Promise<ApiResponse<LC_BulkRejectIncidentsOut>> {
    return employeeIncidentBulkRejectService.bulkReject(input);
  }
}

export const employeeIncidentService = new EmployeeIncidentService();
